import {getLocalStorage} from "./storage";
import {LoginProps} from "../types/authority";

export const randoMessageId = () => {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 16; i++) {
        result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return result;
}

export const generateSignature = async (secret: string, data: string | object): Promise<string> => {
    if (!secret) return '';
    
    try {
        const strData = typeof data === 'string' ? data : JSON.stringify(data);
        
        if (process.env.REACT_APP_ENV === 'dev' || typeof crypto === 'undefined' || !crypto.subtle) {
            const CryptoJS = await import('crypto-js');
            const encrypted = CryptoJS.AES.encrypt(strData, secret).toString();
            return encrypted;
        }
        const encoder = new TextEncoder();
        
        const keyBytes = new Uint8Array(32);
        const secretBytes = encoder.encode(secret);

        keyBytes.set(secretBytes.subarray(0, 32));

        const keyMaterial = await crypto.subtle.importKey(
            'raw',
            keyBytes,
            { name: 'AES-GCM' },
            false,
            ['encrypt']
        );
        
        const iv = crypto.getRandomValues(new Uint8Array(12));
        const encrypted = await crypto.subtle.encrypt(
            { name: 'AES-GCM', iv },
            keyMaterial,
            encoder.encode(strData)
        );
        
        // 将iv和加密数据合并返回
        const result = new Uint8Array(iv.length + encrypted.byteLength);
        result.set(iv, 0);
        result.set(new Uint8Array(encrypted), iv.length);
        return Array.from(result).map(b => b.toString(16).padStart(2, '0')).join('');
    } catch (error) {
        console.error('Reversible signature generation failed:', error);
        return '';
    }
};

export const decryptSignature = async (secret: string, signature: string): Promise<any> => {
    if (!secret || !signature) return null;
    
    try {
        if (process.env.REACT_APP_ENV === 'dev' || typeof crypto === 'undefined' || !crypto.subtle) {
            const CryptoJS = await import('crypto-js');

            const decodedSignature = decodeURIComponent(signature);

            const decrypted = CryptoJS.AES.decrypt(
                decodedSignature, 
                secret,
                { format: CryptoJS.format.OpenSSL } // 明确指定格式
            );
            const decryptedStr = decrypted.toString(CryptoJS.enc.Utf8);
            return decryptedStr ? JSON.parse(decryptedStr) : null;
        }

        const encoder = new TextEncoder();
        const decoder = new TextDecoder();
        
        // 从签名中提取iv和加密数据
        const bytes = new Uint8Array(signature.match(/[\da-f]{2}/gi)!.map(h => parseInt(h, 16)));
        const iv = bytes.slice(0, 12);
        const encrypted = bytes.slice(12);
        
        // 确保密钥长度为256位(32字节)
        const keyBytes = new Uint8Array(32);
        const secretBytes = encoder.encode(secret);
        keyBytes.set(secretBytes.subarray(0, 32));

        const keyMaterial = await crypto.subtle.importKey(
            'raw',
            keyBytes,
            { name: 'AES-GCM' },
            false,
            ['decrypt']
        );
        
        const decrypted = await crypto.subtle.decrypt(
            { name: 'AES-GCM', iv },
            keyMaterial,
            encrypted
        );
        
        return JSON.parse(decoder.decode(decrypted));
    } catch (error) {
        console.error('Signature decryption failed:', error);
        return null;
    }
};

export const getUserInfo = () => {
    const useInfoStr = getLocalStorage('userInfo');
    return useInfoStr ? JSON.parse(useInfoStr) as LoginProps : null
}

export const IsVisitor = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('boardId') && urlParams.get('inspire') && !getUserInfo();
}

export const generateDeviceId = () => {
    return 'device_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
};